import { useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  OneEmptyState,
  ResourceHeader,
  Spinner,
  Tabs,
} from '@factorialco/f0-react/dist/experimental'
import { Archive, ArchiveOpen, Delete, Pencil, People } from '@factorialco/f0-react/icons/app'
import { ItAssetDetailsQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import {
  ASSET_STATUS,
  getDeletedStatusTranslation,
  getDeletedStatusVariant,
  safeAssetStatusAsVariant,
  safeAssetTranslateStatus,
  safeAssetTranslateType,
} from 'modules/it_management/components/AssetsList/utils'
import { ChangeAssetOwnershipModal } from 'modules/it_management/components/ChangeAssetOwnership/ChangeAssetOwnershipModal'
import { DeleteItAssetModal } from 'modules/it_management/components/DeleteItAsset/DeleteItAssetModal'
import { ItAssetCompleteMaintenanceModal } from 'modules/it_management/components/ItAssetCompleteMaintenanceModal/ItAssetCompleteMaintenanceModal'
import { ItAssetMaintenanceModal } from 'modules/it_management/components/ItAssetMaintenanceModal/ItAssetMaintenanceModal'
import { RecoverItAssetModal } from 'modules/it_management/components/RecoverItAsset/RecoverItAssetModal'
import { RetireItAssetModal } from 'modules/it_management/components/RetireItAsset/RetireItAssetModal'
import EditItAssetPopup from 'modules/it_management/pages/EditItAssetPopup'

import { useAssetDetails } from './useAssetDetails'

type Asset = ItAssetDetailsQuery['itManagement']['itAsset']

const AssetDetailsLayout = () => {
  const { navigation } = useNavigation()
  const { assetId } = useParams<{ assetId: string }>()
  const { asset, loading, error, refreshAsset } = useAssetDetails(assetId)
  const [assetToDelete, setAssetToDelete] = useState<Asset | null>(null)
  const [assetToRetire, setAssetToRetire] = useState<Asset | null>(null)
  const [assetToRecover, setAssetToRecover] = useState<Asset | null>(null)
  const [showEditPopup, setShowEditPopup] = useState(false)
  const [assignOwner, setAssignOwner] = useState<Asset | null>(null)
  const [assetToMaintenance, setAssetToMaintenance] = useState<Asset | null>(null)
  const [assetToCompleteMaintenance, setAssetToCompleteMaintenance] = useState<Asset | null>(null)
  const isUnderMaintenance = asset?.status === ASSET_STATUS.MAINTENANCE
  const isDeleted = asset ? 'discardedAt' in asset && !!asset.discardedAt : false

  const handleDeleteAsset = (asset: Asset) => {
    if (!asset) return
    setAssetToDelete(asset)
  }

  const handleRetireAsset = (asset: Asset) => {
    if (!asset) return
    setAssetToRetire(asset)
  }

  const handleRecoverAsset = (asset: Asset) => {
    if (!asset) return
    setAssetToRecover(asset)
  }

  const handleEditAsset = () => {
    setShowEditPopup(true)
  }

  const handleCloseEditPopup = () => {
    setShowEditPopup(false)
  }

  const handleAssignOwner = (asset: Asset) => {
    if (!asset) return
    track(trackingEvents.itManagement.assigmentEquipment.startDetail)
    setAssignOwner(asset)
  }

  const handleSetMaintenance = (asset: Asset) => {
    if (!asset) return
    setAssetToMaintenance(asset)
  }

  const handleCompleteMaintenance = (asset: Asset) => {
    if (!asset) return
    setAssetToCompleteMaintenance(asset)
  }

  if (loading) {
    return (
      <Box justifyContent="center" alignItems="center" padding="s48" width="full">
        <Spinner size="large" />
      </Box>
    )
  }

  if (error) {
    return (
      <Box padding="s24">
        <OneEmptyState
          title={i18n.t('it_management.common.error_occurred')}
          description={error}
          variant="critical"
        />
      </Box>
    )
  }

  if (!asset) {
    return (
      <Box padding="s24">
        <OneEmptyState
          title={i18n.t('it_management.asset_details.asset_not_found')}
          description={i18n.t('it_management.asset_details.asset_not_found_description')}
          variant="critical"
        />
      </Box>
    )
  }

  return (
    <>
      <Box className="flex flex-col h-screen overflow-hidden">
        <Box className="flex-shrink-0">
          <ResourceHeader
            metadata={[
              {
                label: i18n.t('it_management.common.type'),
                value: {
                  type: 'text',
                  content: safeAssetTranslateType(asset.itAssetModel?.typeName ?? ''),
                },
              },
              ...(asset.owner
                ? [
                    {
                      label: i18n.t('it_management.common.assigned_to'),
                      value: {
                        type: 'avatar' as const,
                        text: `${asset.owner?.firstName || ''} ${
                          asset.owner?.lastName || ''
                        }`.trim(),
                        variant: {
                          type: 'person' as const,
                          firstName: asset.owner?.firstName || '',
                          lastName: asset.owner?.lastName || '',
                          src: buildUrl(asset.owner?.avatar?.url ?? ''),
                        },
                      },
                    },
                  ]
                : asset.workplace
                  ? [
                      {
                        label: i18n.t('it_management.common.assigned_to'),
                        value: {
                          type: 'avatar' as const,
                          text: asset.workplace.name || '',
                          variant: {
                            type: 'team' as const,
                            name: asset.workplace.name || '',
                            src: '',
                          },
                        },
                      },
                    ]
                  : asset.team
                    ? [
                        {
                          label: i18n.t('it_management.common.assigned_to'),
                          value: {
                            type: 'avatar' as const,
                            text: asset.team.name || '',
                            variant: {
                              type: 'team' as const,
                              name: asset.team.name || '',
                              src: '',
                            },
                          },
                        },
                      ]
                    : asset.location
                      ? [
                          {
                            label: i18n.t('it_management.common.assigned_to'),
                            value: {
                              type: 'avatar' as const,
                              text: asset.location.name || '',
                              variant: {
                                type: 'team' as const,
                                name: asset.location.name || '',
                                src: '',
                              },
                            },
                          },
                        ]
                      : []),
            ]}
            otherActions={
              isDeleted
                ? []
                : [
                    ...(asset.status === ASSET_STATUS.RETIRED
                      ? [
                          {
                            label: i18n.t('it_management.assets.actions.restore_asset'),
                            icon: ArchiveOpen,
                            onClick: () => {
                              handleRecoverAsset(asset)
                            },
                          },
                        ]
                      : [
                          {
                            label: i18n.t('it_management.assets.actions.retire_asset'),
                            icon: Archive,
                            onClick: () => {
                              handleRetireAsset(asset)
                            },
                          },
                        ]),
                    {
                      label: i18n.t('it_management.assets.actions.delete_asset'),
                      icon: Delete,
                      onClick: () => {
                        handleDeleteAsset(asset)
                      },
                      critical: true,
                    },
                  ]
            }
            primaryAction={
              isDeleted
                ? undefined
                : {
                    label: i18n.t('it_management.assets.actions.edit_assignment'),
                    icon: People,
                    onClick: () => {
                      handleAssignOwner(asset)
                    },
                  }
            }
            secondaryActions={
              isDeleted
                ? []
                : [
                    isUnderMaintenance
                      ? {
                          label: i18n.t('it_management.assets.actions.complete_maintenance'),
                          onClick: () => {
                            handleCompleteMaintenance(asset)
                          },
                        }
                      : {
                          label: i18n.t('it_management.assets.actions.set_maintenance'),
                          onClick: () => {
                            handleSetMaintenance(asset)
                          },
                        },
                    {
                      icon: Pencil,
                      label: i18n.t('it_management.assets.actions.edit'),
                      onClick: handleEditAsset,
                    },
                  ]
            }
            status={{
              label: i18n.t('it_management.common.status'),
              text: isDeleted
                ? getDeletedStatusTranslation()
                : safeAssetTranslateStatus(asset.status),
              variant: isDeleted
                ? getDeletedStatusVariant()
                : safeAssetStatusAsVariant(asset.status),
            }}
            title={
              asset.itAssetModel?.name
                ? `${asset.itAssetModel?.brand} ${asset.itAssetModel?.name}`
                : i18n.t('it_management.assets.actions.asset_with_id', { id: assetId })
            }
          />

          <Box className="[&_nav[aria-label='secondary-navigation']>*:not(a)]:!flex-none">
            <Tabs
              tabs={[
                {
                  href: navigation.it_management.assetId.fullPath({ assetId: assetId || '' }),
                  label: i18n.t('it_management.asset_details.overview'),
                  index: true,
                },
                {
                  href: navigation.it_management.assetId.documents.fullPath({
                    assetId: assetId || '',
                  }),
                  label: i18n.t('it_management.asset_details.documents.title'),
                },
              ]}
            />
          </Box>
        </Box>

        <Box className="flex-1 overflow-y-auto min-h-0">
          <Outlet
            context={{
              asset,
              assetId,
              refreshAsset,
            }}
          />
        </Box>
      </Box>

      {assetToRecover && (
        <RecoverItAssetModal asset={assetToRecover} onClose={() => setAssetToRecover(null)} />
      )}

      {assetToRetire && (
        <RetireItAssetModal asset={assetToRetire} onClose={() => setAssetToRetire(null)} />
      )}

      {assetToDelete && (
        <DeleteItAssetModal asset={assetToDelete} onClose={() => setAssetToDelete(null)} />
      )}

      {showEditPopup && asset && (
        <EditItAssetPopup
          assetId={assetId}
          refreshAsset={refreshAsset}
          onClose={handleCloseEditPopup}
        />
      )}

      {assignOwner && (
        <ChangeAssetOwnershipModal asset={assignOwner} onClose={() => setAssignOwner(null)} />
      )}
      {assetToMaintenance && (
        <ItAssetMaintenanceModal
          asset={assetToMaintenance}
          onClose={() => setAssetToMaintenance(null)}
        />
      )}
      {assetToCompleteMaintenance && (
        <ItAssetCompleteMaintenanceModal
          asset={assetToCompleteMaintenance}
          onClose={() => setAssetToCompleteMaintenance(null)}
        />
      )}
    </>
  )
}

export default AssetDetailsLayout
